import { set } from "date-fns";
import { useRouter } from "next/navigation";
import React, { useState, useEffect, useRef } from "react";
import {
  FaQuestionCircle,
  FaLightbulb,
  FaArrowRight,
  FaUniversity,
  FaClipboardList,
  FaChartLine,
  FaSyncAlt,
  FaFileAlt,
  FaBalanceScale,
  FaMapSigns,
  FaSearch,
  FaHandsHelping,
  FaBell,
  FaChartBar,
} from "react-icons/fa"; // Import icons
import Image from "next/image"; // Import Image component for optimized images
import arrowImage from "../../public/assets/images/challenges/Arrow.svg";
import Choosing from "../../public/assets/images/challenges/Choosing.webp";
import UnderStanding from "../../public/assets/images/challenges/Understanding.webp";
import Predicting from "../../public/assets/images/challenges/Predicting.webp";
import Handling from "../../public/assets/images/challenges/Handling.webp";
import Document from "../../public/assets/images/challenges/Document.webp";
import Private from "../../public/assets/images/challenges/Private.webp";
import College from "../../public/assets/images/challenges/College.webp";
import CollegePredictor from "../../public/assets/images/challenges/NEETSupport.webp";
import Counselling_Gudiance from "../../public/assets/images/challenges/State_Mcc.webp";
import Expert from "../../public/assets/images/challenges/Expert.webp";
import Real_time from "../../public/assets/images/challenges/Real-Time.webp";
import Previous from "../../public/assets/images/challenges/Previous.webp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons"; // Import specific icon

const challenges = [
  {
    title: "Choosing Medical College",
    imageRight: Choosing, // Image for Choosing Medical College
    icon: <FaUniversity className="text-[#FF9245] text-xl mr-2" />, // Icon for Choosing Medical College
    topic: "Choosing the Right Medical College",
    button: "College Search",
    description: [
      "It is important to choose a medical college that meets one's career goals, budget, and personal preferences.",
      "When making this choice, things like recognition, good faculty, facilities, hospital exposure, and location matter a lot.",
      "Making an informed decision helps you have a better college experience and sets the right path for a strong medical career.",
    ],
  },
  {
    title: "Counselling Processes",
    imageRight: UnderStanding,
    icon: <FaClipboardList className="text-[#FF9245] text-xl mr-2" />, // Icon for Counselling Processes
    topic: "Understanding MCC & State Counselling Processes",
    button: "Counselling",
    description: [
      "The Medical Counselling Committee (MCC) oversees the All India Quota (AIQ) counselling for 15% of seats in government medical colleges, while respective state authorities manage the remaining 85% under the state quota.",
      "Each has distinct procedures, eligibility criteria, and timelines.",
      "Misunderstanding these processes can lead to missed opportunities or errors in application.",
    ],
  },
  {
    title: "Predicting NEET Rank",
    imageRight: Predicting,
    icon: <FaChartLine className="text-[#FF9245] text-xl mr-2" />, // Icon for Predicting NEET Rank
    topic: "Predicting Chances Based on NEET Rank",
    button: "Rank Predictor",
    description: [
      "To know which colleges you can get with your NEET rank, you need to check last year’s cutoffs, seat availability, and reservation rules.",
      "Without the right info, you might choose a college that’s too hard to get or miss out on a better one.",
      "Right tools, clear predictions, and expert help make it easier to choose colleges that fit your rank and support your medical career.",
    ],
  },
  {
    title: "Counselling Rounds",
    imageRight: Handling,
    icon: <FaSyncAlt className="text-[#FF9245] text-xl mr-2" />, // Icon for Counselling Rounds
    topic: "Handling Multiple Counselling Rounds",
    button: "Counselling",
    description: [
      "NEET counselling involves multiple rounds, including mop-up rounds, to fill vacant seats.",
      "Each round has its registration, choice filling, and allotment processes.",
      "Keeping track of these rounds, understanding the rules of upgradation, and making timely decisions are challenging yet crucial to avoid losing a potential seat.",
    ],
  },
  {
    title: "Document Verification",
    imageRight: Document,
    icon: <FaFileAlt className="text-[#FF9245] text-xl mr-2" />, // Icon for Document Verification
    topic: "Document Verification & Deadlines Confusion",
    button: "Document Checklist",
    description: [
      "Each counselling authority mandates specific documents for verification, and missing any can lead to disqualification.",
      "Additionally, when different counselling rounds have deadlines close to each other, it can get confusing.",
      "To avoid any last-minute issues, it's important to stay updated and keep all your documents ready on time.",
    ],
  },
  {
    title: "College Selection",
    imageRight: Private,
    icon: <FaBalanceScale className="text-[#FF9245] text-xl mr-2" />, // Icon for College Selection
    topic: "Private vs. Government College Selection",
    button: "College Predictor",
    description: [
      "Deciding between private and government medical colleges involves considerations of tuition fees, infrastructure, clinical exposure, and future career opportunities.",
      "Government colleges often offer affordable fees but have higher cutoffs, while private colleges may have better facilities but come with higher costs.",
      "It's important to compare these factors with your own goals and budget to make the right choice for your future.",
    ],
  },
];

const neetSupportHelp = [
  {
    title: "Navigation Dashboard",
    imageRight: College,
    icon: <FaMapSigns className="text-[#FF9245] text-xl mr-2" />, // Icon for Navigation Dashboard
    topic: "College & Seat Navigation Dashboard",
    button: "Seat Matrix",
    description: [
      "Our easy-to-use dashboard allows candidates to explore medical colleges based on their NEET rank, preferences, and eligibility.",
      "It provides detailed information on college infrastructure, faculty, patient inflow, and more, enabling aspirants to make informed choices that match their career goals.",
    ],
  },
  {
    title: "College Predictor",
    imageRight: CollegePredictor,
    icon: <FaSearch className="text-[#FF9245] text-xl mr-2" />, // Icon for College Predictor
    topic: "NEET Support College Predictor",
    button: "College Predictor",
    description: [
      "NEET Support’s College Predictor uses past data and smart technology to show you which colleges you can get with your NEET rank.",
      "It helps you set clear goals and plan your choices wisely—so you don’t miss out on the right college.",
    ],
  },
  {
    title: "Counselling Guidance",
    imageRight: Counselling_Gudiance,
    icon: <FaHandsHelping className="text-[#FF9245] text-xl mr-2" />, // Icon for Counselling Guidance
    topic: "State & MCC Counselling Guidance",
    button: "Counselling",
    description: [
      "We offer comprehensive guidance on both MCC and state counselling processes, including registration procedures, choice filling strategies, fee structures, and cut-off trends.",
      "Our experts guide you through the tricky counselling process, making sure you have the best chance of getting a seat in your desired college.",
    ],
  },
  {
    title: "Expert Consultation",
    imageRight: Expert,
    icon: <FaBell className="text-[#FF9245] text-xl mr-2" />, // Icon for Expert Consultation
    topic: "Expert Consultation by Mentors",
    button: "Expert Consultation",
    description: [
      "Our experienced counsellors provide personalized one-on-one sessions to help you make the best decisions for NEET counselling.",
      "Whether it’s picking the right college or managing your finances, we give you clear, expert advice on every step to ensure you’re on the right path to success.",
    ],
  },
  {
    title: "Updates & Alerts",
    imageRight: Real_time,
    icon: <FaBell className="text-[#FF9245] text-xl mr-2" />, // Icon for Updates & Alerts
    topic: "Real-Time Updates & Alerts",
    button: "Updates & Alerts",
    description: [
      "Stay informed with timely notifications about important dates, changes in counselling procedures, and seat allotment results.",
      "Our real-time updates ensure you never miss any important deadlines, keeping you ahead in the competitive admission process.",
    ],
  },
  {
    title: "Cutoff Analysis",
    imageRight: Previous,
    icon: <FaChartBar className="text-[#FF9245] text-xl mr-2" />, // Icon for Cutoff Analysis
    topic: "Previous Year Data & Cutoff Analysis",
    button: "Cutoff Analysis",
    description: [
      "Access detailed analysis of previous years' cutoffs, seat allotments, and admission trends.",
      "This data-driven approach helps in understanding competition levels, setting realistic targets, and making informed decisions during choice filling.",
    ],
  },
];

const NeedHelp = () => {
  const [activeChallengesFAQ, setActiveChallengesFAQ] = useState(0);
  const [activeHelpFAQ, setActiveHelpFAQ] = useState(0);
  const [isChallengesMenuOpen, setIsChallengesMenuOpen] = useState(false);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const router = useRouter();
  const challengesTitleRefs = useRef([]);
  const helpTitleRefs = useRef([]);

  const handleNavigation = (title) => {
    const routes = {
      "Choosing Medical College":
        "https://neetsupport.com/collegeList",
      "Counselling Processes":
        "https://portal.neetsupport.com/dashboard-Counselling",
      "Predicting NEET Rank":
        "https://neetsupport.com/neet-rank-predictor",
      "Counselling Rounds":
        "https://portal.neetsupport.com/dashboard-Counselling",
      "Document Verification":
        "https://portal.neetsupport.com/dashboard-document-Checklist",
      "College Selection":
        "https://portal.neetsupport.com/dashboard-College-Predictor",
      "Navigation Dashboard":
        "https://portal.neetsupport.com/dashboard-fees-seatmatrix",
      "College Predictor":
        "https://neetsupport.com/neet-college-predictor",
      "Counselling Guidance":
        "https://portal.neetsupport.com/dashboard-Counselling",
      "Expert Consultation": "/services",
      "Updates & Alerts": "/blogs",
      "Cutoff Analysis": "https://portal.neetsupport.com/dashboard-cut-offs",
    };
    return routes[title] || "/";
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isChallengesMenuOpen &&
        !event.target.closest(".challenges-menu") &&
        !event.target.closest(".challenges-toggle")
      ) {
        setIsChallengesMenuOpen(false);
      }
      if (
        isHelpMenuOpen &&
        !event.target.closest(".help-menu") &&
        !event.target.closest(".help-toggle")
      ) {
        setIsHelpMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isChallengesMenuOpen, isHelpMenuOpen]);

  const handleChallengesClick = (index) => {
    setActiveChallengesFAQ(index);
    setIsChallengesMenuOpen(false);
    challengesTitleRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: index === challenges.length - 1 ? "end" : "center",
    });
  };

  const handleHelpClick = (index) => {
    setActiveHelpFAQ(index);
    setIsHelpMenuOpen(false);
    helpTitleRefs.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: index === neetSupportHelp.length - 1 ? "end" : "center",
    });
  };

  return (
    <section className="layout-pt-md layout-pb-md bg-beige-1">
      <div className="modal-header text-center">
        <h2 className="font-semibold text-3xl text-[#140342]">
          Need Help in
          <span className="text-[#FF9245]"> NEET Counselling</span>
        </h2>
        <p className="text-gray-600 mt-2 text-lg">
          The <span className="font-bold">NEET counselling process</span> is
          often complicated. Here’s a simple overview to help students{" "}
          <span className="font-bold">avoid mistakes</span> and{" "}
          <span className="font-bold">choose wisely</span>.
        </p>
      </div>

      {/* Desktop View */}
      <div className="modal-container my-8 mx-auto hidden md:block">
        <h3 className="text-[#140342] my-4 text-2xl flex justify-center font-semibold">
          Common Challenges Faced by{" "}
          <span className="text-[#ff9245]">&nbsp;NEET Aspirants</span>
        </h3>
        <div className="hidden md:flex">
          {/* Left Section: Titles */}
          <div className="w-1/5 border-gray-300 pr-4" role="tablist" aria-orientation="vertical">
            {challenges.map((faq, index) => (
              <div
                key={index}
                ref={(el) => (challengesTitleRefs.current[index] = el)}
                className={`rounded-lg p-4 cursor-pointer text-sm shadow-md font-medium mt-2 mb-2 flex items-center transition-all duration-300 ${
                  activeChallengesFAQ === index
                    ? "bg-white text-[#ff9245] border-l-4 border-[#ff9245] font-bold shadow-lg"
                    : "text-[#140342] hover:bg-gray-100"
                }`}
                onClick={() => handleChallengesClick(index)}
                tabIndex={0}
                aria-selected={activeChallengesFAQ === index}
                role="tab"
              >
                {faq.icon} {faq.title}
              </div>
            ))}
          </div>

          {/* Right Section: Content and Image */}
          <div className="w-4/5 flex bg-white rounded-lg shadow-md p-6">
            {/* Only render the active challenge ONCE */}
            <div className="w-full xl:w-2/3 pr-6 flex flex-col justify-between">
              {(() => {
                const faq = challenges[activeChallengesFAQ];
                return (
                  <div aria-hidden={false} tabIndex={0} role="tabpanel">
                    <h3 className="text-[20px] mb-6 text-left font-semibold text-[#140342]">
                      {faq.topic}
                    </h3>
                    <ul className="list-none text-md space-y-2 text-gray-700">
                      {faq.description.map((item, i) => (
                        <li key={i} className="flex items-start leading-loose">
                          <span className="text-[#FF9245] mr-2">→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-center items-center mt-6">
                      <button
                        onClick={() =>
                          window.open(
                            faq.title ? handleNavigation(faq.title) : "/",
                            "_blank"
                          )
                        }
                        className="px-4 py-2 bg-[#FF9245] text-white rounded-3xl hover:bg-[#fff] hover:ring-1 hover:ring-[#ff9245] hover:!text-[#ff9245] transition-all duration-300 flex justify-center items-center"
                      >
                        {faq.button}
                        <FontAwesomeIcon
                          icon={faArrowUpRightFromSquare}
                          className="ml-2 text-sm"
                        />
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="hidden xl:flex w-1/3 justify-center items-center">
              {(() => {
                const faq = challenges[activeChallengesFAQ];
                return (
                  <div
                    className="w-full h-64 rounded-lg flex items-center relative justify-center transition-shadow duration-300"
                    aria-hidden={false}
                  >
                    {faq.imageRight ? (
                      <Image
                        src={faq.imageRight}
                        alt={faq.title}
                        className="rounded-lg object-contain"
                        layout="fill"
                      />
                    ) : (
                      <span className="text-gray-500 text-lg">
                        [Image Placeholder]
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      <div className="modal-container my-8 mx-auto hidden md:block">
        <h3 className="text-[#140342] my-4 text-2xl flex justify-center font-semibold">
          How &nbsp;<span className="text-[#ff9245]">NEET Support</span>
          &nbsp;Can Help You
        </h3>
        <div className="hidden md:flex">
          {/* Left Section: Titles */}
          <div className="w-1/5 border-gray-300 pr-4" role="tablist" aria-orientation="vertical">
            {neetSupportHelp.map((faq, index) => (
              <div
                key={index}
                ref={(el) => (helpTitleRefs.current[index] = el)}
                className={`rounded-lg p-4 cursor-pointer text-sm shadow-md font-medium mt-2 mb-2 flex items-center transition-all duration-300 ${
                  activeHelpFAQ === index
                    ? "bg-white text-[#ff9245] border-l-4 border-[#ff9245] font-bold shadow-lg"
                    : "text-[#140342] hover:bg-gray-100"
                }`}
                onClick={() => handleHelpClick(index)}
                tabIndex={0}
                aria-selected={activeHelpFAQ === index}
                role="tab"
              >
                {faq.icon} {faq.title}
              </div>
            ))}
          </div>

          {/* Right Section: Content and Image */}
          <div className="w-4/5 flex bg-white rounded-lg shadow-md p-6">
            {/* Only render the active help ONCE */}
            <div className="w-full xl:w-2/3 pr-6 flex flex-col justify-between">
              {(() => {
                const faq = neetSupportHelp[activeHelpFAQ];
                return (
                  <div aria-hidden={false} tabIndex={0} role="tabpanel">
                    <h3 className="text-[20px] mb-6 text-left font-semibold text-[#140342]">
                      {faq.topic}
                    </h3>
                    <ul className="list-none text-md space-y-2 text-gray-700">
                      {faq.description.map((item, i) => (
                        <li key={i} className="flex items-start leading-loose">
                          <span className="text-[#FF9245] mr-2">→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-center items-center mt-6">
                      <button
                        onClick={() =>
                          window.open(
                            faq.title ? handleNavigation(faq.title) : "/",
                            "_blank"
                          )
                        }
                        className="px-4 py-2 bg-[#FF9245] text-white rounded-3xl hover:bg-[#fff] hover:ring-1 hover:ring-[#ff9245] hover:!text-[#ff9245] transition-all duration-300 flex justify-center items-center"
                      >
                        {faq.button}
                        <FontAwesomeIcon
                          icon={faArrowUpRightFromSquare}
                          className="ml-2 text-sm"
                        />
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="hidden xl:flex w-1/3 justify-center items-center">
              {(() => {
                const faq = neetSupportHelp[activeHelpFAQ];
                return (
                  <div
                    className="w-full h-64 rounded-lg flex items-center relative justify-center transition-shadow duration-300"
                    aria-hidden={false}
                  >
                    {faq.imageRight ? (
                      <Image
                        src={faq.imageRight}
                        alt={faq.title}
                        className="rounded-lg object-contain"
                        layout="fill"
                      />
                    ) : (
                      <span className="text-gray-500 text-lg">
                        [Image Placeholder]
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="relative flex flex-col md:hidden p-4 mx-4 bg-white rounded-lg shadow-md mt-8">
        <div className="modal-header flex items-center">
          <button
            className="absolute right-0 transform -translate-y-1/2 text-white p-2 rounded-l-lg shadow-md challenges-toggle"
            onClick={() => setIsChallengesMenuOpen(!isChallengesMenuOpen)}
          >
            <Image
              src={arrowImage}
              alt="Toggle Challenges Menu"
              className={`transform transition-transform duration-300 ${
                isChallengesMenuOpen ? "rotate-180" : ""
              } animate-bounce`}
              width={25}
              height={25}
            />
          </button>
          <h3 className="text-[#140342] text-xl font-semibold">
            Common Challenges Faced by{" "}
            <span className="text-[#ff9245]">NEET Aspirants</span>
          </h3>
        </div>
        {isChallengesMenuOpen && (
          <div className="absolute right-0 top-0 h-full bg-gray-100 rounded-l-lg shadow-md p-4 z-10 challenges-menu">
            {challenges.map((faq, index) => (
              <div
                key={index}
                className={`p-2 cursor-pointer text-sm font-medium rounded-lg mb-2 flex items-center ${
                  activeChallengesFAQ === index
                    ? "bg-[#FF9245] text-white"
                    : "bg-white text-[#140342] hover:bg-gray-200"
                }`}
                onClick={() => handleChallengesClick(index)}
              >
                <span
                  className={`mr-2 ${
                    activeChallengesFAQ === index
                      ? "text-white"
                      : "text-[#FF9245]"
                  }`}
                >
                  {React.cloneElement(faq.icon, {
                    className:
                      activeChallengesFAQ === index
                        ? "text-white"
                        : "text-[#FF9245]",
                  })}
                </span>
                {faq.title}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          {challenges.map((faq, index) => (
            <div
              key={index}
              className={activeChallengesFAQ === index ? "" : "hidden"}
              aria-hidden={activeChallengesFAQ !== index}
              tabIndex={activeChallengesFAQ === index ? 0 : -1}
            >
              <h3 className="text-lg font-semibold text-[#140342] mt-2 mb-4">
                {faq.topic}
              </h3>
              <ul className="list-none ml- text-gray-700">
                {faq.description.map((item, i) => (
                  <li key={i} className="flex items-start mb-2">
                    <span className="text-[#FF9245] mr-2">→</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex justify-center items-center mt-8">
                <button
                  onClick={() =>
                    window.open(
                      faq.title ? handleNavigation(faq.title) : "/",
                      "_blank"
                    )
                  }
                  className="px-4 py-2 bg-[#FF9245] text-white rounded-3xl hover:bg-[#FF5722] transition-all duration-300 flex justify-center items-center"
                >
                  {faq.button}
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="ml-2 text-sm"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative flex flex-col md:hidden px-4 py-4 mx-4 bg-white rounded-lg shadow-md mt-8">
        <div className="modal-header flex items-center">
          <button
            className="absolute right-0 transform -translate-y-1/2 text-white p-2 rounded-l-lg shadow-md help-toggle"
            onClick={() => setIsHelpMenuOpen(!isHelpMenuOpen)}
          >
            <Image
              src={arrowImage}
              alt="Toggle Help Menu"
              className={`transform transition-transform duration-300 ${
                isHelpMenuOpen ? "rotate-180" : ""
              } animate-bounce`}
              width={25}
              height={25}
            />
          </button>
          <h3 className="text-[#140342] text-xl font-semibold ml-12">
            How <span className="text-[#ff9245]"> NEET Support </span>
            Can Help You
          </h3>
        </div>
        {isHelpMenuOpen && (
          <div className="absolute right-0 top-0 h-full bg-gray-100 rounded-l-lg shadow-md p-4 z-10 help-menu">
            {neetSupportHelp.map((faq, index) => (
              <div
                key={index}
                className={`p-2 cursor-pointer text-sm font-medium rounded-lg mb-2 flex items-center ${
                  activeHelpFAQ === index
                    ? "bg-[#FF9245] text-white"
                    : "bg-white text-[#140342] hover:bg-gray-200"
                }`}
                onClick={() => handleHelpClick(index)}
              >
                <span
                  className={`mr-2 ${
                    activeHelpFAQ === index ? "text-white" : "text-[#FF9245]"
                  }`}
                >
                  {React.cloneElement(faq.icon, {
                    className:
                      activeHelpFAQ === index ? "text-white" : "text-[#FF9245]",
                  })}
                </span>
                {faq.title}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          {neetSupportHelp.map((faq, index) => (
            <div
              key={index}
              className={activeHelpFAQ === index ? "" : "hidden"}
              aria-hidden={activeHelpFAQ !== index}
              tabIndex={activeHelpFAQ === index ? 0 : -1}
            >
              <h3 className="text-lg font-semibold text-[#140342] mb-4 mt-2">
                {faq.topic}
              </h3>
              <ul className="list-none ml-2 text-gray-700">
                {faq.description.map((item, i) => (
                  <li key={i} className="flex items-start mb-2">
                    <span className="text-[#FF9245] mr-2">→</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="flex justify-center items-center mt-8">
                <button
                  onClick={() =>
                    window.open(
                      faq.title ? handleNavigation(faq.title) : "/",
                      "_blank"
                    )
                  }
                  className="px-4 py-2 bg-[#FF9245] text-white rounded-3xl hover:bg-[#FF5722] transition-all duration-300 flex justify-center items-center"
                >
                  {faq.button}
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="ml-2 text-sm"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NeedHelp;
